import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { translateText } from "../../lib/translate";

const CODE_TO_LABEL = {
  en: "language.english",
  es: "language.spanish",
  ht: "language.haitianCreole",
  pt: "language.portuguese",
  he: "language.hebrew",
};

/**
 * TranslatedText — renders user-generated text in the viewer's preferred
 * language when the source language differs. The ORIGINAL is always preserved
 * and one tap away.
 *
 *   • Same language as viewer  → original shown, no indicator.
 *   • Different language       → translation shown + "Translated from X · View
 *                                original" toggle.
 *   • Monthly cap reached      → original shown + a small "translation paused"
 *                                notice (never hidden, never blocked).
 *
 * Props: text (string), className (applied to the text span), testId.
 */
export default function TranslatedText({ text, className = "", testId }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [res, setRes] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    let alive = true;
    setRes(null);
    setShowOriginal(false);
    if (text && text.trim() && user?.preferred_language) {
      translateText(text).then((r) => { if (alive) setRes(r); });
    }
    return () => { alive = false; };
  }, [text, user?.preferred_language]);

  const srcLabel = res?.source && CODE_TO_LABEL[res.source] ? t(CODE_TO_LABEL[res.source]) : "";
  const isTranslated = !!res?.translated;
  const capReached = !!res?.cap_reached;
  const display = isTranslated && !showOriginal ? res.translated_text : text;

  return (
    <span className={className} data-testid={testId}>
      <span data-testid={testId ? `${testId}-body` : undefined}>{display}</span>
      {isTranslated && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowOriginal((v) => !v); }}
          data-testid={testId ? `${testId}-toggle` : "tx-toggle"}
          className="mt-1 flex items-center gap-1 text-[10px] font-mono-accent text-zinc-400 hover:text-white transition"
        >
          <Languages size={11} className="opacity-70" />
          {showOriginal
            ? t("chat.viewTranslation")
            : `${t("chat.translatedFrom", { lang: srcLabel })} · ${t("chat.viewOriginal")}`}
        </button>
      )}
      {capReached && !isTranslated && (
        <span
          className="mt-1 block text-[10px] font-mono-accent text-amber-500/80"
          data-testid={testId ? `${testId}-cap` : "tx-cap"}
        >
          {t("chat.translationPaused")}
        </span>
      )}
    </span>
  );
}
